import { theme } from '@openx/components-core/lib/theme/theme.ts';
import type { DealParticipant, SynchronizedWith } from '@openx/types/deal.ts';
import {
  type DV360FailureReason,
  DV360FailureReasons,
  type DV360Seller,
  type SynchronizationStatus,
  synchronizationStatuses,
} from '@openx/types/deal.ts';
import type { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';

export const getDV360Status = (
  isSynced: boolean,
  isRejected: boolean,
  isFailed: boolean
): SynchronizationStatus | null => {
  if (isSynced) return synchronizationStatuses.SYNCED;
  if (isRejected) return synchronizationStatuses.REJECTED;
  if (isFailed) return synchronizationStatuses.FAILED;

  return null;
};

export const getTTDStatus = (hasId: boolean, isSynced: boolean): SynchronizationStatus | null => {
  if (!hasId) return null;

  return isSynced ? synchronizationStatuses.SYNCED : synchronizationStatuses.FAILED;
};

export const getDspStatus = (
  dv360Status: SynchronizationStatus | null,
  ttdStatus: SynchronizationStatus | null
): SynchronizationStatus => {
  const dv360 = dv360Status ?? synchronizationStatuses.NONE;
  const ttd = ttdStatus ?? synchronizationStatuses.NONE;

  if (dv360 !== synchronizationStatuses.SYNCED || ttd !== synchronizationStatuses.SYNCED) {
    return dv360 > ttd ? dv360 : ttd;
  }

  return synchronizationStatuses.SYNCED;
};

export const getStatusColor = (status: SynchronizationStatus) => {
  switch (status) {
    case synchronizationStatuses.SYNCED:
      return theme.palette.success.main;
    case synchronizationStatuses.REJECTED:
      return theme.palette.warning.main;
    case synchronizationStatuses.FAILED:
      return theme.palette.error.main;
    default:
      return theme.palette.text.secondary;
  }
};

export const mapDV360StatusToMessage = (
  t: TFunction,
  status: SynchronizationStatus | null,
  dv360seller?: DV360Seller | null
): string => {
  if (!dv360seller || status === null) return '';

  switch (status) {
    case synchronizationStatuses.SYNCED:
      return t('Synced, ID: {order_name}', { order_name: dv360seller.order_name });

    case synchronizationStatuses.REJECTED:
      return dv360seller.reject_date
        ? t('Rejected by DSP, {reject_date}, ID: {order_name}', {
            order_name: dv360seller.order_name,
            reject_date: dv360seller.reject_date,
          })
        : t('Rejected by DSP, ID: {order_name}', { order_name: dv360seller.order_name });

    case synchronizationStatuses.FAILED:
      return dv360seller.failure_date
        ? t('Sync failed, {failure_date}, ID: {order_name}', {
            failure_date: dv360seller.failure_date,
            order_name: dv360seller.order_name,
          })
        : t('Sync failed, ID: {order_name}', { order_name: dv360seller.order_name });

    default:
      return '';
  }
};

export const mapTTDStatusToMessage = (
  t: TFunction,
  status: SynchronizationStatus | null,
  ttd_proposal_id?: string | null
): string => {
  if (!ttd_proposal_id || status === null) return '';

  switch (status) {
    case synchronizationStatuses.SYNCED:
      return t('Synced, ID: {ttd_proposal_id}', { ttd_proposal_id });
    case synchronizationStatuses.FAILED:
      return t('Sync failed');
    default:
      return '';
  }
};

export const mapDV360FailureReasonToMessage = (t: TFunction, failureReason?: DV360FailureReason | null): string => {
  if (!failureReason) return '';

  switch (failureReason) {
    case DV360FailureReasons.ORDER_CANCELLED:
      return t('Reason: Order has been cancelled in DV360.');
    default:
      return '';
  }
};

export const hasDv360Seller = (sync?: SynchronizedWith | null): boolean => !!sync && !isEmpty(sync.dv360seller);

export const hasDv360OrderName = (sync?: SynchronizedWith | null): boolean =>
  hasDv360Seller(sync) && !!sync?.dv360seller?.order_name;

export const hasDv360RejectReason = (sync?: SynchronizedWith | null): boolean =>
  hasDv360Seller(sync) && !!sync?.dv360seller?.reject_reason;

export const hasDv360FailureReason = (sync?: SynchronizedWith | null): boolean =>
  hasDv360Seller(sync) && !!sync?.dv360seller?.failure_reason;

export const hasTtdProposalId = (sync?: SynchronizedWith | null): boolean => !!sync?.ttd_proposal_id?.toString();

export const isValidDMP = (deal_participants: DealParticipant[], dmpId: string): boolean =>
  deal_participants.some(participant => participant.demand_partner === dmpId);
